import {
  Badge,
  CircularProgress,
  Menu,
  Tooltip,
} from "@mui/material";
import { OverlayPanel } from "primereact/overlaypanel";
import React, { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import NotificationIcon from "../../assets/icon_components/notificationIcon";
import MenuIcon from "../../assets/images/menuIcon.svg";
import SearchIconVm from "../../assets/icon_components/searchIcon";
import {
  updateNotificationCount,
  userActivities,
} from "../../services/activities";
import { getUserProfile } from "../../services/profile";
import {
  capitalizeFirstLetter,
} from "../../utils";
import {
  itemTitlePlural,
  logout,
  getRoleDisplayName,
} from "../../utils/constant";
import { useWindowSize } from "../../utils/windowResize";
import NavHead from "./NavHead";
import { SearchBox } from "../SearchBox";
import { useUserAccountLogoutMutation } from "../../features/login/loginapi.services";
import { useMQTTContext } from "../../services/mqttService";
import { useGlobalSearch } from "../../hooks/useGlobalSearch";

const NAV_DESKTOP_MIN_WIDTH = 768;
const NAV_COMPACT_SEARCH_MAX_WIDTH = 1024;

const AdminSettingsMenu = lazy(
  () => import("../../component/navbar/AdminSettingsMenu")
);
const NavbarUserMenu = lazy(() => import("../../component/navbar/NavbarUserMenu"));
const NavbarLogoutDialog = lazy(
  () => import("../../component/navbar/NavbarLogoutDialog")
);
const Notification = lazy(() => import("../../component/Notification"));
const MobileDrawer = lazy(() => import("../../component/MobileDrawer"));
const ApplicationNotification = lazy(() =>
  import("../../component/Notification/SystemNotification").then((module) => ({
    default: module.ApplicationNotification,
  }))
);

export const Navbar = () => {
  const { width } = useWindowSize();
  const isDesktopNav = width >= NAV_DESKTOP_MIN_WIDTH;
  const isCompactSearchLabel = width < NAV_COMPACT_SEARCH_MAX_WIDTH;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { pathname } = location;
  const searchBar = useRef(null);
  const [overlayCurrent, setOverlayCurrent] = useState(null);
  const { subscribedTopics, unsubscribe } = useMQTTContext();
  const {
    search,
    searchType,
    searchData,
    loading: searchLoading,
    filterOptions,
    handleSearchChange,
    handleSearchTypeChange,
    getResultName,
    navigateToResult,
  } = useGlobalSearch();
  // States
  const [hamburgerOpen, setHamburgerOpen] = useState(false);
  const [isLogout, setIsLogout] = useState(false);
  const [anchorNotifyEl, setNotifyAnchorEl] = useState(null);
  const [notificationCount, setNotificationCount] = useState(0);
  // Selectors
  const userInfo = useSelector((state) => state.userInfo);
  const { activity } = useSelector((state) => state.activity);
  const { profile } = useSelector((state) => state.profileInfo);
  const { notificationCount: notificationCountFromStore } = useSelector(
    (state) => state.activity
  );
  // Derived values
  const role = userInfo.data.role || "";

  const usesType = getRoleDisplayName(role)
  const openNotifyMenu = Boolean(anchorNotifyEl);
 
  const isUserRegistered = userInfo?.data?.is_registered;
  const isApproved =
    userInfo?.data?.userinfo?.approve_status === "accept" ||
    role === "admin";
  const isSuperAdmin = userInfo?.data?.role === "super_admin";
  const [userAccountLogout] = useUserAccountLogoutMutation();
  // Menu handlers
  const handleNotifyIconClick = (event) => {
    if (!isDesktopNav) {
      navigate("/notification");
      return;
    }
    setNotifyAnchorEl(event.currentTarget);
  };
  const handleMoreClose = () => setNotifyAnchorEl(null);
  const handleHamburgerClick = () => setHamburgerOpen((prev) => !prev);

  // Navigation handlers
  const handleLogout = () => {
    logout({ userAccountLogout, dispatch, navigate, subscribedTopics, unsubscribe });
  };

  const handleLogoClick = () => {
   
        if (role === "mentee" && !isUserRegistered) {
          navigate("/individual");
        } else if (userInfo?.data?.role === "super_admin" && isUserRegistered) {
          // No action for super admin
        } else if (role === "mentor" && !isUserRegistered) {
          navigate("/questions");
        } 
        else if(role === "hta"){
          navigate("/programs?program_type=field_session")
        } 
        else {
          navigate("/dashboard?type=yettojoin");
        }
  };

  const handleOpenSearchBar = (e) => {
    if (!document.getElementById("search_overlay_panel")) {
      searchBar.current.toggle(e);
    }
    setOverlayCurrent(e);
  };

  const handleSearchNavigation = (item) => {
    navigateToResult(item, {
      onBeforeNavigate: () => {
        searchBar.current?.toggle(overlayCurrent);
        const input = document.getElementById("search-navbar");
        if (input) input.value = "";
      },
    });
  };

  useEffect(() => {
    if (activity?.notifications_count) {
      setNotificationCount(activity?.notifications_count);
      dispatch(
        updateNotificationCount({
          notificationCount: activity?.notifications_count,
        })
      );
    }
  }, [activity]);

  // Effects
  useEffect(() => {
    if (!Object.keys(profile).length) {
      dispatch(getUserProfile());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(userActivities({ limit: 10 }));
  }, []);

  // Render search input and dropdown
  const renderSearchInput = () => (
    <div className="relative search-container">
      <div>   
        <label htmlFor="search-navbar" className="sr-only">
          {isCompactSearchLabel
            ? `${itemTitlePlural}`
            : `Search ${itemTitlePlural}/ `}
        </label>
        <SearchBox
          height="40px"
          onClick={handleOpenSearchBar}
          value={search}
          handleSearch={handleSearchChange}
          animatedItems={[itemTitlePlural, "Provider", "Medical Assistant", "Healthcare Transportation Associate" ]}
          enableAnimation={true}
          width="300px" // Full width on mobile, component handles responsive sizing
        />

        <OverlayPanel
          ref={searchBar}
          id="search_overlay_panel"
          style={{ width:  "300px", top: "63px !important" }}
          className="notification-container !bg-white searchbar-container"
          onClose={() => console.log("Close")}
        >
          <div className="flex gap-1 justify-evenly p-2">
            {filterOptions.map((btn) => (
              <button
                key={btn.key}
                onClick={() => handleSearchTypeChange(btn.key)}
                className={
                  searchType === btn.key ? "active-info" : ""
                }
              >
                {btn.name}
              </button>
            ))}
          </div>
          <div>
            {searchLoading && (
              <div className="flex flex-col items-center justify-center p-4">
                <CircularProgress
                  size={24}
                  style={{ color: "var(--color-bg-primary-main)" }}
                />
                <p className="mt-2 text-gray-600">Searching...</p>
              </div>
            )}
            {!searchLoading &&
            searchData.length === 0 &&
            search?.trim() ? (
              <div className="text-center text-gray-500 p-4">No data found</div>
            ) : (
              <ul>
                {searchData.map((item, i) => {
                  const name = getResultName(item);
                  return (
                    <li
                      key={i}
                      className="cursor-pointer truncate w-full block py-3 px-2"
                      onClick={() => handleSearchNavigation(item)}
                      title={name}
                      style={{
                        maxWidth: "100%",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                      }}
                    >
                      {name}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </OverlayPanel>
      </div>
    </div>
  );

  return (
    <div
      className="navbar-content px-4 max-md:px-2 max-md:py-2 bg-white"
      style={{ boxShadow: "4px 4px 25px 0px rgba(0, 0, 0, 0.15)" }}
    >
      <Suspense fallback={null}>
        <ApplicationNotification />
      </Suspense>
      {isLogout && (
        <Suspense fallback={null}>
          <NavbarLogoutDialog
            open={isLogout}
            onClose={() => setIsLogout(false)}
            onConfirm={handleLogout}
          />
        </Suspense>
      )}
        <nav className="bg-white border-gray-200">
          <div className="flex justify-between">
            {/* Left Section - Logo and Super Admin Menu */}
            <div className="contain gap-8 sm:gap-8 md:gap-12 lg:gap-24 xl:gap-24  justify-between w-[60%] md:w-[40%] p-0 sm:p-0 md:p-2 lg:p-2 xl:p-2 mr-5">
              <NavHead role={role} handleLogoClick={handleLogoClick} />
            </div>

            {/* Right Section - Search, Notifications, Settings, User Menu */}
            <div className="flex items-center justify-center p-4 gap-2 max-md:p-1 max-md:gap-1">
              {/* Search Bar */}
              {pathname !== "/search" && (
                <div
                  className="md:hidden mr-2 cursor-pointer"
                  onClick={() => navigate("/search")}
                  role="button"
                  aria-label="Search"
                >
                  <SearchIconVm />
                </div>
              )}
              <div className="hidden md:flex shrink-0">
              {isUserRegistered && isApproved && renderSearchInput()}
              </div>
              
              {/* Notifications */}
              {isUserRegistered &&
                !isSuperAdmin &&
                (isDesktopNav || pathname !== "/notification") && (
                <div className="relative">
                  <div className="notitification-group pr-2">
                    <div
                      className="rounded-[3px] h-[40px] w-[40px] max-sm:h-[35px] max-sm:w-[35px] flex items-center justify-center bg-background-primary-light"
                      onClick={handleNotifyIconClick}
                    >
                      {notificationCountFromStore > 0 ? (
                        <Badge
                          color="error"
                          badgeContent={notificationCountFromStore}
                          variant="standard"
                          sx={{ cursor: "pointer" }}
                          max={99}
                        >
                          <NotificationIcon className="cursor-pointer notification-image" />
                        </Badge>
                      ) : (
                        <NotificationIcon className="cursor-pointer notification-image" />
                      )}
                    </div>

                    {isDesktopNav && (
                    <Menu
                      anchorEl={anchorNotifyEl}
                      open={openNotifyMenu}
                      onClose={handleMoreClose}
                      transformOrigin={{ horizontal: "right", vertical: "top" }}
                      anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                      slotProps={{
                        paper: {
                          elevation: 1,
                          sx: {
                            overflow: "visible",
                            mt: 1.5,
                            padding: "2px",
                            "&::before": {
                              content: '""',
                              display: "block",
                              position: "absolute",
                              top: 0,
                              right: 14,
                              width: 10,
                              height: 10,
                              bgcolor: "#fff",
                              transform: "translateY(-50%) rotate(45deg)",
                              zIndex: -1,
                              boxShadow: "0px 0px 2px rgba(0,0,0,0.2)",
                            },
                            width: { xs: "95vw", sm: "450px" },
                            maxWidth: { xs: "95vw", sm: "450px" },
                            minWidth: { xs: "320px", sm: "400px" },
                            maxHeight: { xs: "80vh", sm: "600px" },
                            "@media (max-width: 640px)": {
                              position: "fixed",
                              left: "50%",
                              transform: "translateX(-50%)",
                              mt: 1,
                              ml: 0,
                              // Hide arrow on mobile
                              "&::before": {
                                display: "none",
                              },
                            },

                            // Ensure notification container doesn't create horizontal scroll
                            "& .notification-container": {
                              maxWidth: "100%",
                              overflowX: "hidden",
                            },
                          },
                        },
                      }}
                    >
                      {openNotifyMenu && (
                        <Suspense
                          fallback={
                            <div className="flex items-center justify-center p-6">
                              <CircularProgress size={24} />
                            </div>
                          }
                        >
                          <Notification
                            handleClose={handleMoreClose}
                            countSetter={setNotificationCount}
                          />
                        </Suspense>
                      )}
                    </Menu>
                    )}
                  </div>
                </div>
              )}

              {/* Settings Menu */}
              {role === "admin" && (
                <div className="hidden md:flex shrink-0">
                  <Suspense
                    fallback={
                      <span
                        className="inline-block h-8 w-8 shrink-0"
                        aria-hidden
                      />
                    }
                  >
                    <AdminSettingsMenu />
                  </Suspense>
                </div>
              )}

              <div
                className="hidden md:flex min-h-[2.5rem] w-[7.5rem] max-w-[11rem] shrink-0 flex-col items-end justify-center sm:w-auto"
                aria-hidden={!isDesktopNav}
              >
                <span className="w-full truncate text-end text-[14px] font-semibold leading-tight">
                  {[profile.first_name, profile.middle_name, profile.last_name]
                    .filter(Boolean)
                    .join(" ") || "\u00A0"}
                </span>
                <span className="w-full truncate text-end text-xs leading-tight text-background-primary-main">
                  {usesType ? capitalizeFirstLetter(usesType) : "\u00A0"}
                </span>
              </div>
             
              <div className="hidden h-8 w-8 shrink-0 md:block">
                <Suspense
                  fallback={
                    <span
                      className="inline-block h-8 w-8 shrink-0"
                      aria-hidden
                    />
                  }
                >
                  <NavbarUserMenu onLogoutRequest={() => setIsLogout(true)} />
                </Suspense>
              </div>

              <div className="md:hidden">
                  <div className="h-[50px] items-center ">
                    <Tooltip title="Hamburger Menu">
                      <button
                        className="p-1 w-full h-full "
                        onClick={handleHamburgerClick}
                      >
                        <img
                          src={MenuIcon}
                          className="size-6 items-center"
                          alt="hamburger"
                        />
                      </button>
                    </Tooltip>
                  </div>
                  {hamburgerOpen && (
                    <Suspense fallback={null}>
                      <MobileDrawer
                        isOpen={hamburgerOpen}
                        onClose={() => setHamburgerOpen(false)}
                      />
                    </Suspense>
                  )}
              </div>
            </div>
          </div>
        </nav>
    </div>
  );
};
